// License service
// - Check user's license status (web, app, or none)
// - Initiate web license purchase via Stripe
// - Activate app license after Google Play purchase

import { supabase } from "../lib/supabase";
import { PlayIntegrity } from "../plugins/playIntegrity";

export interface LicenseStatus {
  hasWebLicense: boolean;
  hasAppLicense: boolean;
  canCreateHousehold: boolean;
  isLoading: boolean;
}

export class LicenseService {
  // Check user's current license status
  async getLicenseStatus(): Promise<LicenseStatus> {
    try {
      const { data: user } = await supabase.auth.getUser();

      if (!user.user) {
        return {
          hasWebLicense: false,
          hasAppLicense: false,
          canCreateHousehold: false,
          isLoading: false,
        };
      }

      const { data, error } = await supabase
        .from("profiles")
        .select("has_web_license, has_app_license")
        .eq("id", user.user.id)
        .single();

      if (error) {
        console.error("🔴 Error fetching license:", error);
        return {
          hasWebLicense: false,
          hasAppLicense: false,
          canCreateHousehold: false,
          isLoading: false,
        };
      }

      const hasWebLicense = data?.has_web_license ?? false;
      const hasAppLicense = data?.has_app_license ?? false;

      return {
        hasWebLicense,
        hasAppLicense,
        canCreateHousehold: hasWebLicense || hasAppLicense,
        isLoading: false,
      };
    } catch (error) {
      console.error("🔴 Error in getLicenseStatus:", error);
      return {
        hasWebLicense: false,
        hasAppLicense: false,
        canCreateHousehold: false,
        isLoading: false,
      };
    }
  }

  // Purchase web license via Stripe
  // This calls the Supabase Edge Function which creates a Stripe Checkout session
  // The user is then redirected to Stripe's hosted checkout page
  async purchaseWebLicense(): Promise<void> {
    try {
      // Call Edge Function to create checkout session
      const { data, error } = await supabase.functions.invoke(
        "create-web-checkout",
        {
          body: {},
        },
      );

      if (error) throw error;

      if (!data?.url) {
        throw new Error("No checkout URL returned");
      }

      // Redirect to Stripe Checkout
      window.location.href = data.url;
    } catch (error) {
      console.error("🔴 Purchase error:", error);
      throw error;
    }
  }

  // Activate app license after Google Play purchase
  // Called from the native app when user opens it after purchasing
  async activateAppLicense(
    platform: "google_play" | "app_store",
  ): Promise<void> {
    try {
      if (platform === "google_play") {
        // Pantry is a paid Play Store listing, not an in-app purchase, so
        // there's no purchase token to verify. Instead we prove the app was
        // actually acquired from Google Play via a Play Integrity token:
        // 1. Ask the server for a signed, single-use nonce.
        // 2. Ask the OS to mint an attestation token bound to that nonce.
        // 3. Send the token to the server, which decodes it with Google and
        //    checks the account's licensing verdict before activating.
        const { data: nonceData, error: nonceError } =
          await supabase.functions.invoke("request-integrity-nonce", {
            body: {},
          });

        if (nonceError) throw nonceError;
        if (!nonceData?.nonce) {
          throw new Error("No nonce returned from server");
        }

        const { token } = await PlayIntegrity.requestIntegrityToken({
          nonce: nonceData.nonce,
        });

        const { error } = await supabase.functions.invoke(
          "activate-app-license",
          {
            body: {
              platform,
              integrityToken: token,
            },
          },
        );

        if (error) throw error;
      } else {
        // TODO: iOS currently trusts that the app was installed from a paid
        // App Store listing. Consider App Store receipt validation (or
        // DeviceCheck/App Attest) for stronger verification.
        const { error } = await supabase.functions.invoke(
          "activate-app-license",
          {
            body: {
              platform,
              purchaseToken: "app-purchased",
            },
          },
        );

        if (error) throw error;
      }

      console.log(`🟢 App license activated for ${platform}`);
    } catch (error) {
      console.error("🔴 Activation error:", error);
      throw error;
    }
  }
}

export const licenseService = new LicenseService();
